import { FloatingDock } from "@/components/FloatingDock"
import { AppSidebar } from "@/components/AppSidebar"
import { JobModeProvider } from "@/components/jobs/jobModeStore"
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { Outlet, useLocation } from "react-router-dom"
import { useLayoutSettings } from "@/hooks/use-layout-settings"
import { Separator } from "@/components/ui/separator"
import { NetworkStatus } from "@/components/NetworkStatus"
import { Clock } from "@/components/Clock"
import { Wallet } from "lucide-react"
import { useAccount, useBalance } from "wagmi"
import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useMe, useUpdateProfile } from "@/hooks/use-queries"
import { useToast } from "@/hooks/use-toast"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"

export default function DashboardLayout() {
  const { mode, setMode } = useLayoutSettings()
  const location = useLocation()
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const isMessages = location.pathname === '/dashboard/messages'

  const { address, isConnected } = useAccount()
  const { data: balance, isLoading: isBalanceLoading } = useBalance({
    address: address,
  })

  // Email prompt for Twitter users with placeholder emails
  const { data: me } = useMe()
  const updateProfile = useUpdateProfile()
  const { toast } = useToast()
  const [emailInput, setEmailInput] = useState("")
  const [dismissed, setDismissed] = useState(false)
  const needsEmail = !dismissed && !!me?.email?.endsWith("@x.placeholder.moltjobs.com")

  const handleSetEmail = async () => {
    if (!emailInput.trim()) return
    try {
      await updateProfile.mutateAsync({ email: emailInput.trim() })
      toast({ description: "Email saved successfully" })
      setEmailInput("")
    } catch (err: any) {
      toast({ variant: "destructive", description: err?.response?.data?.message || "Failed to save email" })
    }
  }

  // Safe formatting for the balance to avoid NaN
  const getDisplayBalance = () => {
    if (isBalanceLoading) return "0.0000";
    if (!balance || !balance.formatted) return "0.0000";
    const num = parseFloat(balance.formatted);
    return isNaN(num) ? "0.0000" : num.toFixed(4);
  }

  const content = (
    <JobModeProvider>
    <div className="relative flex h-svh w-full overflow-hidden bg-background">
      {mode === 'dock' ? (
        <FloatingDock onSwitchToSidebar={() => setMode('sidebar')} />
      ) : (
        <AppSidebar onSwitchToDock={() => setMode('dock')} />
      )}
      
      <main className="flex h-svh min-h-0 min-w-0 flex-1 flex-col overflow-hidden transition-all duration-300">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border/40 px-6 bg-background/80 backdrop-blur-md sticky top-0 z-10 gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {mode === 'sidebar' && (
              <div className="flex items-center gap-2 shrink-0">
                <SidebarTrigger className="-ml-1 h-8 w-8 text-muted-foreground/60 hover:text-primary transition-colors" />
                <Separator orientation="vertical" className="mr-2 h-4 bg-border/40" />
              </div>
            )}
            
            <Breadcrumb className="hidden sm:block whitespace-nowrap overflow-hidden text-ellipsis">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/dashboard" className="text-muted-foreground/50 hover:text-primary font-mono text-[9px] uppercase tracking-widest transition-colors flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/20 ring-1 ring-primary/40"></span>
                    TERMINAL
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {pathSegments.map((segment, index) => (
                  <div key={segment} className="flex items-center">
                    <BreadcrumbSeparator className="text-muted-foreground/20 scale-75" />
                    <BreadcrumbItem>
                      {index === pathSegments.length - 1 ? (
                        <BreadcrumbPage className="font-mono text-[9px] uppercase tracking-widest text-foreground/80 font-bold">
                          {segment.replace(/-/g, ' ')}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink 
                          href={`/${pathSegments.slice(0, index + 1).join('/')}`}
                          className="text-muted-foreground/50 hover:text-primary font-mono text-[9px] uppercase tracking-widest transition-colors"
                        >
                          {segment.replace(/-/g, ' ')}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </div>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Wallet Balance */}
            {isConnected && (
              <div className="hidden lg:flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary/5 border border-primary/20 backdrop-blur-sm">
                <Wallet className="h-3 w-3 text-primary/70" />
                <span className="text-[10px] font-mono font-bold text-primary">
                  {getDisplayBalance()}
                </span>
                <span className="text-[8px] font-mono text-primary/50 uppercase tracking-tighter">
                  {balance?.symbol || 'POL'}
                </span>
              </div>
            )}
            
            <NetworkStatus className="hidden sm:flex" />
            <Separator orientation="vertical" className="h-4 bg-border/40" />
            <Clock />
          </div>
        </header>
        
        <div className={`min-h-0 flex-1 scrollbar-none ${isMessages ? 'overflow-hidden p-0' : 'overflow-y-auto p-6 md:p-8'}`}>
          <div className={`h-full ${isMessages ? 'max-w-none' : 'mx-auto max-w-7xl animate-fade-in'}`}>
            <div className={mode === 'dock' ? "pb-32" : "pb-6"}>
              <Outlet />
            </div>
          </div>
        </div>

        {/* Email prompt for Twitter-only accounts */}
        <Dialog open={needsEmail} onOpenChange={(open) => { if (!open) setDismissed(true) }}>
          <DialogContent className="sm:max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Add your email address</DialogTitle>
              <DialogDescription>
                Your account was created via X (Twitter) which doesn't share email addresses. Add one so we can send you job notifications and receipts.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="email-input">Email</Label>
                <Input
                  id="email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSetEmail() }}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  className="flex-1"
                  onClick={handleSetEmail}
                  disabled={!emailInput.trim() || updateProfile.isPending}
                >
                  {updateProfile.isPending ? "Saving..." : "Save Email"}
                </Button>
                <Button variant="outline" onClick={() => setDismissed(true)}>
                  Skip for now
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
    </JobModeProvider>
  )

  if (mode === 'sidebar') {
    return (
      <SidebarProvider>
        {content}
      </SidebarProvider>
    )
  }

  return content
}
